import { z } from 'zod';

/**
 * A calendar date, validated as a date and not merely as a shape.
 *
 * ⭐ It lives in a LEAF module — importing nothing from the other schema files — because
 * `asset.ts` (where it used to live), `fleet.ts` and `spine.ts` all need it, and `asset.ts`
 * imports `idempotency` FROM `spine.ts`. Having `spine.ts` reach back for it closed a cycle:
 * `idempotency` was `undefined` at the moment `asset.ts` evaluated, and every schema built
 * on it threw `Cannot read properties of undefined (reading 'extend')` at import time.
 *
 * 🔴 TypeScript did not catch that, and could not: the import was type-erased at compile
 * time and only existed at runtime. `tsc --noEmit` passed on both packages while the module
 * graph was broken. Only running the tests found it.
 *
 * The alternative — a second copy of these three lines next to the new caller — is exactly
 * what the original docstring warned against, because the second copy is where the
 * `isRealDate` refinement gets left off.
 */

// The regex alone admits '2026-13-40', which then dies in the DB as a 500 — the shape
// AND the calendar are both this layer's job (review 106 m1).
// Round-trip the components: Date.parse ROLLS impossible dates ('2026-02-30' becomes
// March 2nd and parses fine), so a NaN check is a guard that can't fail for the common
// cases (adversary 106 F2 — proven with a Feb 30th that reached the DB as a 500).
export const isRealDate = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, day!));
  return (
    dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === day
  );
};

export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-07-30')
  .refine(isRealDate, 'Not a real calendar date');
